import { ASSISTANT_OP_SPECS } from '@/interfaces/assistant.interface';
import type {
  IAssistantOpDto,
  TAssistantOpName,
} from '@/interfaces/assistant.interface';
import { assistantCanToggle } from '../tools/tool-gate';
import type { IGpthelpToolGate } from '../tools/tool-types';

/**
 * Why the assistant cannot prepare a change here (plan F10): the op is
 * switched off, the member's role lacks prices, other edits or this
 * branch's inventory, it needs every branch, or the store's plan lacks it.
 */
export type TAssistantLock =
  | 'disabled'
  | 'catalog'
  | 'edit'
  | 'stock'
  | 'allBranches'
  | 'plan';

/**
 * Ops that change a regular or discounted price (plan F10; each phase adds
 * its own). An option choice's price changes through updateOptionChoice,
 * which is not one of them: its server refusal names only the op, so it
 * reads as any other edit (`isPriceChange` reads its fields, plan P3-4).
 */
export const PRICE_OPS: readonly TAssistantOpName[] = [
  'setPrice',
  'adjustPrices',
  'setDiscount',
  'setPrices',
];

/**
 * Ops whose `branchScope: 'all'` reaches every branch; the Action API
 * ignores the field on the others.
 */
const BRANCH_SCOPE_OPS: readonly TAssistantOpName[] = [
  'setAvailability',
  'setCategoryAvailability',
];

/**
 * Ops whose `available: false` creates something hidden in every branch
 * (plan P2-1): only a member over every branch can show it again later.
 */
const CREATE_OPS: readonly TAssistantOpName[] = [
  'createItem',
  'createCategory',
];

/**
 * Ops that change categories themselves (plan P2-1, P4-2): their lock cards
 * speak of categories (W13 decision). Moves and product orders keep the
 * product words.
 */
const CATEGORY_OPS: readonly TAssistantOpName[] = [
  'createCategory',
  'renameCategory',
  'setCategoryDescription',
  'setCategoryAvailability',
  'setCategoryPosOnly',
  'reorderCategories',
  'setCategorySchedule',
];

/**
 * The role locks a permission lock card shows: prices, other edits, this
 * branch's inventory, every branch.
 */
export type TPermissionLock = Extract<
  TAssistantLock,
  'catalog' | 'edit' | 'stock' | 'allBranches'
>;

/**
 * The title of a permission lock card of `op` (plan F10): prices; this
 * branch's inventory (plan P5-3); a category op's own words (W13 decision:
 * changing categories, showing or hiding them, in every branch); else the
 * product words. Without an op (a refusal that names none), the product
 * words.
 */
export function permissionLockKey(
  lock: TPermissionLock,
  op: TAssistantOpName | undefined
): string {
  if (lock === 'catalog') return 'assistant.lock.catalog';
  if (lock === 'stock') return 'assistant.lock.stock';
  if (op === undefined || !CATEGORY_OPS.includes(op))
    return lock === 'allBranches'
      ? 'assistant.lock.allBranches'
      : 'assistant.lock.edit';
  if (lock === 'allBranches') return 'assistant.lock.allBranchesCategory';
  return ASSISTANT_OP_SPECS[op].permission === 'toggle'
    ? 'assistant.lock.toggleCategory'
    : 'assistant.lock.editCategory';
}

/**
 * What a permission lock card of `op` adds that the assistant can still do
 * for this member (plan F10, W13 decision): show or hide categories, for a
 * category op while setCategoryAvailability is offered here; else show or
 * hide products while setAvailability is; null when neither.
 */
export function lockToggleKey(
  op: TAssistantOpName | undefined,
  gate: Pick<IGpthelpToolGate, 'canDisableCatalog' | 'assistantOps'>
): string | null {
  if (
    op !== undefined &&
    CATEGORY_OPS.includes(op) &&
    gate.canDisableCatalog &&
    gate.assistantOps.includes('setCategoryAvailability')
  )
    return 'assistant.lock.canToggleCategory';
  return assistantCanToggle(gate) ? 'assistant.lock.canToggle' : null;
}

/**
 * The plan lock title of each op a plan gates (plan F10; each phase adds
 * its own). setPrices is offered only on Plus: its title is for a plan drop
 * between propose and apply (the server's refusal).
 */
export const PLAN_LOCK_KEYS: Readonly<
  Partial<Record<TAssistantOpName, string>>
> = {
  adjustPrices: 'assistant.lock.bulkPlan',
  setPrices: 'assistant.lock.plan.setPrices',
  setItemSchedule: 'assistant.lock.plan.schedule',
  setCategorySchedule: 'assistant.lock.plan.schedule',
  // Inventory is on Plus (plan P5-1, the UI's `canStock`).
  recordStock: 'assistant.lock.plan.stock',
  createStockItem: 'assistant.lock.plan.stock',
  updateStockItem: 'assistant.lock.plan.stock',
  setRecipe: 'assistant.lock.plan.stock',
};

/** The plan lock title of an op without its own key. */
const PLAN_LOCK_FALLBACK_KEY = 'assistant.lock.bulkPlan';

/** The plan lock title of `op` (`PLAN_LOCK_KEYS`, else the bulk-price one). */
export function planLockKey(op: TAssistantOpName | undefined): string {
  return (op && PLAN_LOCK_KEYS[op]) || PLAN_LOCK_FALLBACK_KEY;
}

/**
 * The op changes what a product, size or option choice costs (its role needs
 * CatalogManage): a price op, or an updateOptionChoice that sends a price or
 * a discounted price (plan P3-4: pausing or renaming a choice is not).
 */
export function isPriceChange(
  op: Pick<IAssistantOpDto, 'op' | 'price' | 'discountedPrice'>
): boolean {
  return (
    PRICE_OPS.includes(op.op) ||
    (op.op === 'updateOptionChoice' &&
      (op.price !== undefined || op.discountedPrice !== undefined))
  );
}

/** The flags of the gate `lockOf` reads. */
export type TAssistantLockGate = Pick<
  IGpthelpToolGate,
  | 'assistantDisabledOps'
  | 'canManageCatalog'
  | 'canDisableCatalog'
  | 'canHideEverywhere'
  | 'effectiveTier'
>;

/**
 * What stops the assistant from preparing `op` for this member and store,
 * read from the op's spec (the admin UI's own gates, plan F1); null when
 * nothing does. The Action API enforces the same rules: this is the fast
 * feedback that shows the lock card before any request. The member's own
 * locks (role, every branch, plan) come before the switch: the switched-off
 * card tells the member to make the change on the Menú themselves, which
 * only helps a member whose role, branches and plan allow it.
 */
export function lockOf(
  op: IAssistantOpDto,
  gate: TAssistantLockGate
): TAssistantLock | null {
  const spec = ASSISTANT_OP_SPECS[op.op];
  switch (spec.permission) {
    case 'manage':
      if (!gate.canManageCatalog) return isPriceChange(op) ? 'catalog' : 'edit';
      break;
    case 'toggle':
      if (!gate.canDisableCatalog) return 'edit';
      break;
    case 'stock':
      // CatalogManage in this branch, like every stock page (plan P5-1).
      if (!gate.canManageCatalog) return 'stock';
      break;
    default: {
      const unknownPermission: never = spec.permission;
      throw new Error(`Unknown op permission: ${String(unknownPermission)}`);
    }
  }
  // Created hidden = hidden in every branch: only a member over every
  // branch can show it again later.
  const everyBranch =
    spec.allBranches ||
    (BRANCH_SCOPE_OPS.includes(op.op) && op.branchScope === 'all') ||
    (CREATE_OPS.includes(op.op) && op.available === false);
  if (everyBranch && !gate.canHideEverywhere) return 'allBranches';
  if (spec.minTier > gate.effectiveTier) return 'plan';
  if (gate.assistantDisabledOps.includes(op.op)) return 'disabled';
  return null;
}
